import { Alert, Button, Card, Form, Input, message, Space, Typography, Upload } from "antd"
import { observer } from "mobx-react"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { UsersViewModel } from "./UsersViewModel"
import { useNavigate } from "react-router-dom"
import { useMemo, useState } from "react"
import { RoutePaths } from "../../navigation/RoutePaths.enum"
import { useTranslation } from "react-i18next"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import { UploadOutlined } from "@ant-design/icons"

const { Item } = Form
const { Text } = Typography

const MAX_ROWS = 50000
const SUBJECT_MAX_LENGTH = 180
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

const stripHtml = (value: string) =>
	value
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.trim()

const quillModules = {
	toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]]
}

const UsersNotifyBulkView = () => {
	const { t } = useTranslation("users", { keyPrefix: "notifyBulk" })
	const navigate = useNavigate()
	const viewModel = useContainerInjection<UsersViewModel>("UsersViewModel")

	const [csvFile, setCsvFile] = useState<File | null>(null)
	const [csvError, setCsvError] = useState<string>("")
	const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
	const [attachmentError, setAttachmentError] = useState("")
	const [subject, setSubject] = useState("")
	const [messageHtml, setMessageHtml] = useState("")
	const [sending, setSending] = useState(false)
	const [notificationSent, setNotificationSent] = useState(false)

	const subjectCounter = useMemo(() => `${subject.length}/${SUBJECT_MAX_LENGTH}`, [subject.length])
	const templateHref = useMemo(
		() =>
			`data:text/csv;charset=utf-8,${encodeURIComponent(
				"email\nexample1@sporttips.com\nexample2@sporttips.com"
			)}`,
		[]
	)

	const validateCsv = (file: File): Promise<string> =>
		new Promise(resolve => {
			const reader = new FileReader()
			reader.onload = e => {
				const text = (e.target?.result as string) ?? ""
				const lines = text.split("\n").filter(line => line.trim())
				if (lines.length === 0) {
					resolve(t("csvEmpty"))
					return
				}
				const headers = lines[0].split(",").map(h =>
					h
						.trim()
						.toLowerCase()
						.replace(/"/g, "")
						.replace(/^\ufeff/, "")
				)
				if (headers.length !== 1) {
					resolve(t("csvSingleColumn"))
					return
				}
				if (headers[0] !== "email") {
					resolve(t("csvNoEmailColumn"))
					return
				}
				if (lines.length - 1 > MAX_ROWS) {
					resolve(t("csvTooManyRows", { max: MAX_ROWS.toLocaleString() }))
					return
				}
				resolve("")
			}
			reader.onerror = () => resolve(t("csvReadError"))
			reader.readAsText(file)
		})

	const onSubmit = async () => {
		if (!csvFile) {
			message.error(t("csvRequired"))
			return
		}
		if (csvError) {
			message.error(csvError)
			return
		}
		if (!subject.trim()) {
			message.error(t("subjectRequired"))
			return
		}
		if (subject.length > SUBJECT_MAX_LENGTH) {
			message.error(t("subjectMax"))
			return
		}
		if (attachmentError) {
			message.error(attachmentError)
			return
		}
		if (!stripHtml(messageHtml)) {
			message.error(t("messageRequired"))
			return
		}

		setSending(true)
		try {
			await viewModel.sendUsersNotification({
				emailListFile: csvFile,
				subject: subject.trim(),
				messageHtml,
				attachmentFile: attachmentFile || undefined
			})
			setSubject("")
			setMessageHtml("")
			setCsvFile(null)
			setCsvError("")
			setAttachmentFile(null)
			setAttachmentError("")
			setNotificationSent(true)
		} catch {
			message.error(t("sendError"))
		} finally {
			setSending(false)
		}
	}

	return (
		<Card
			bordered={false}
			className="criclebox tablespace mb-24"
			title={<h6 className="font-semibold m-0">{t("title")}</h6>}
			bodyStyle={{ padding: "24px" }}
		>
			{notificationSent && (
				<Alert type="success" message={t("sentSuccess")} showIcon style={{ marginBottom: 16 }} />
			)}

			<Form layout="vertical">
				<Item
					label={t("emailList")}
					required
					validateStatus={csvError ? "error" : csvFile ? "success" : undefined}
					help={
						csvError ? <Text type="danger">{csvError}</Text> : <Text type="secondary">{t("csvHint")}</Text>
					}
				>
					<Space size={8} align="start">
						<Upload
							maxCount={1}
							showUploadList={csvFile !== null}
							fileList={
								csvFile ? [{ uid: "-1", name: csvFile.name, status: csvError ? "error" : "done" }] : []
							}
							onRemove={() => {
								setCsvFile(null)
								setCsvError("")
							}}
							beforeUpload={async file => {
								if (!/\.(csv|txt)$/i.test(file.name)) {
									setCsvError(t("listFileInvalidType"))
									setCsvFile(null)
									return false
								}

								if (file.size > MAX_FILE_SIZE_BYTES) {
									setCsvError(t("listFileMaxSize"))
									setCsvFile(null)
									return false
								}

								// Keep an in-memory snapshot to avoid browser upload errors when the source file changes.
								const fileBuffer = await file.arrayBuffer()
								const fileSnapshot = new File([fileBuffer], file.name, {
									type: file.type || "text/plain"
								})
								const error = await validateCsv(fileSnapshot)
								setCsvError(error)
								setCsvFile(fileSnapshot)
								return false
							}}
						>
							<Button icon={<UploadOutlined />}>{t("selectFile")}</Button>
						</Upload>
						<Button href={templateHref} download="users-email-template.csv" type="link">
							{t("downloadTemplate")}
						</Button>
					</Space>
				</Item>

				<Item
					label={t("attachment")}
					validateStatus={attachmentError ? "error" : undefined}
					help={
						attachmentError ? (
							<Text type="danger">{attachmentError}</Text>
						) : (
							<Text type="secondary">{t("attachmentOptional")}</Text>
						)
					}
				>
					<Upload
						maxCount={1}
						showUploadList={attachmentFile !== null}
						fileList={attachmentFile ? [{ uid: "-3", name: attachmentFile.name, status: "done" }] : []}
						onRemove={() => {
							setAttachmentFile(null)
							setAttachmentError("")
						}}
						beforeUpload={async file => {
							if (file.size > MAX_FILE_SIZE_BYTES) {
								setAttachmentError(t("attachmentMaxSize"))
								setAttachmentFile(null)
								return false
							}

							const fileBuffer = await file.arrayBuffer()
							const fileSnapshot = new File([fileBuffer], file.name, {
								type: file.type || "application/octet-stream"
							})
							setAttachmentError("")
							setAttachmentFile(fileSnapshot)
							return false
						}}
					>
						<Button icon={<UploadOutlined />}>{t("selectAttachment")}</Button>
					</Upload>
				</Item>

				<Item label={t("subject")} required>
					<Input
						value={subject}
						onChange={e => setSubject(e.target.value)}
						maxLength={SUBJECT_MAX_LENGTH}
						placeholder={t("subjectPlaceholder")}
						suffix={subjectCounter}
					/>
				</Item>

				<Item label={t("message")} required>
					<ReactQuill
						className="users-notify-editor"
						value={messageHtml}
						onChange={setMessageHtml}
						modules={quillModules}
					/>
				</Item>

				<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
					<Button onClick={() => navigate(RoutePaths.USERS)}>{t("back")}</Button>
					<Button type="primary" loading={sending} disabled={sending} onClick={onSubmit}>
						{t("send")}
					</Button>
				</div>
			</Form>
		</Card>
	)
}

export default observer(UsersNotifyBulkView)
